import { Box, Chip, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { validateContactIds } from 'services/system-messages.service';
import {
	clearContactIdsValidation,
	getContactIdsValidation,
} from 'state/reducers/system-messages.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getContactIdsFromRaw } from '../createForm.helpers';

interface ContactIdsChipsInputProps {
	rawValue: string;
	onRawChange: (next: string) => void;
}

const DEBOUNCE_MS = 400;

type CompanyAssociation = { id: number; name: string };
type ValidContact = { id: number; name: string; companies: CompanyAssociation[] };

const formatCompanyAssociations = (companies: CompanyAssociation[]): string => {
	return companies.map((company) => `${company.id} - ${company.name}`).join(', ');
};

const formatContactLabel = (contact: ValidContact): string => {
	const companyLabel = contact.companies.length > 0 ? ` (${formatCompanyAssociations(contact.companies)})` : '';
	return `${contact.id} — ${contact.name}${companyLabel}`;
};

const formatSummary = (
	template: string,
	counts: { validCount: number; invalidCount: number; totalValidatedIds: number }
) =>
	template
		.replace('{{valid}}', String(counts.validCount))
		.replace('{{invalid}}', String(counts.invalidCount))
		.replace('{{total}}', String(counts.totalValidatedIds));

export const ContactIdsChipsInput = ({ rawValue, onRawChange }: ContactIdsChipsInputProps) => {
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const validation = useAppSelector(getContactIdsValidation);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const validCount = validation.valid.length;
	const invalidCount = validation.invalid.length;
	const totalValidatedIds = validCount + invalidCount;
	const hasValidationResults = totalValidatedIds > 0;
	const hasInvalidIds = invalidCount > 0;
	const { formatError, ids } = getContactIdsFromRaw(rawValue);
	const invalidChipBackground = alpha(theme.palette.root.error, 0.08);
	const invalidChipBorder = alpha(theme.palette.root.error, 0.18);
	const validChipBorder = alpha(theme.palette.root.mediumGreen, 0.35);
	const summaryText = formatSummary(t('page.systemMessages.contactIds.summary'), {
		validCount,
		invalidCount,
		totalValidatedIds,
	});

	useEffect(() => {
		dispatch(clearContactIdsValidation());

		if (formatError || ids.length === 0) {
			return;
		}

		timerRef.current = setTimeout(() => {
			dispatch(validateContactIds(ids));
		}, DEBOUNCE_MS);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [rawValue, dispatch]);

	return (
		<Box>
			<TextField
				multiline
				minRows={3}
				placeholder={t('page.systemMessages.contactIds.placeholder')}
				value={rawValue}
				onChange={(e) => onRawChange(e.target.value)}
				error={formatError}
				helperText={formatError ? t('validations.errors.integer') : undefined}
				inputProps={{
					'aria-label': t('page.systemMessages.contactIds.placeholder'),
					style: { textAlign: 'right' },
				}}
				fullWidth
				dir="ltr"
				sx={{
					'& .MuiOutlinedInput-root': {
						minHeight: 74,
						alignItems: 'flex-start',
					},
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.palette.root.mediumGreen,
					},
					'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.palette.root.inputFocusGreen,
					},
					'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.palette.root.inputFocusGreen,
					},
				}}
			/>
			{hasValidationResults && (
				<Box
					sx={{
						marginTop: '10px',
						border: '1px solid',
						borderColor: theme.palette.root.mediumGreen,
						borderRadius: '6px',
						background: theme.palette.root.basicWhite,
					}}
				>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{
							padding: '8px 12px',
							borderBottom: '1px solid',
							borderColor: 'divider',
							background: theme.palette.root.basicWhite,
							borderRadius: '6px 6px 0 0',
						}}
					>
						<Typography variant="caption" sx={{ color: theme.palette.root.border_teal }}>
							{summaryText}
						</Typography>
						{hasInvalidIds && (
							<Typography variant="caption" sx={{ color: theme.palette.root.error, fontWeight: 600 }}>
								{t('page.systemMessages.contactIds.saveBlocked')}
							</Typography>
						)}
					</Stack>
					<Stack
						direction="row"
						flexWrap="wrap"
						gap="6px"
						sx={{ padding: '10px 12px', maxHeight: 200, overflowY: 'auto' }}
					>
						{validation.invalid.map((id) => (
							<Chip
								key={`err-${id}`}
								label={`${id} — ${t('page.systemMessages.contactIds.notFound')}`}
								size="small"
								sx={{
									background: invalidChipBackground,
									color: theme.palette.root.error,
									border: `1px solid ${invalidChipBorder}`,
								}}
							/>
						))}
						{validation.valid.map((c) => (
							<Chip
								key={`ok-${c.id}`}
								label={formatContactLabel(c)}
								size="small"
								sx={{
									background: theme.palette.root.basicWhite,
									color: theme.palette.root.formTitle,
									border: `1px solid ${validChipBorder}`,
								}}
							/>
						))}
					</Stack>
				</Box>
			)}
		</Box>
	);
};
